import { useState, useEffect } from "react";
import { FileText, Sparkles, Download, ShieldAlert, Save, Edit2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function OfferLetters() {
    const [documents, setDocuments] = useState([]);
    const [interns, setInterns] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [drafting, setDrafting] = useState(false);

    // Form states
    const [recipientType, setRecipientType] = useState("custom"); // "custom" | "intern" | "employee"
    const [selectedInternId, setSelectedInternId] = useState("");
    const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
    const [name, setName] = useState("");
    const [role, setRole] = useState("");
    const [offerType, setOfferType] = useState("Internship");
    const [duration, setDuration] = useState("3 months");
    const [stipend, setStipend] = useState("10,000 INR / month");
    const [email, setEmail] = useState("");
    const [content, setContent] = useState("");

    // Regeneration states
    const [editingDoc, setEditingDoc] = useState(null);
    const [editContent, setEditContent] = useState("");

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        setLoading(true);
        try {
            const [docsRes, internsRes, empsRes] = await Promise.all([
                api.get("/documents?documentType=offer_letter"),
                api.get("/interns"),
                api.get("/employees")
            ]);
            setDocuments(docsRes.data.items || []);
            setInterns(internsRes.data.items || []);
            setEmployees(empsRes.data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const handleRecipientTypeChange = (val) => {
        setRecipientType(val);
        setName("");
        setRole("");
        setEmail("");
        setSelectedInternId("");
        setSelectedEmployeeId("");
    };

    const handleInternSelect = (id) => {
        setSelectedInternId(id);
        const intern = interns.find((i) => i.id === id);
        if (intern) {
            setName(intern.fullName);
            setRole(intern.role || "Intern");
            setEmail(intern.email || "");
        } else {
            setName("");
            setRole("");
            setEmail("");
        }
    };

    const handleEmployeeSelect = (id) => {
        setSelectedEmployeeId(id);
        const emp = employees.find((e) => e.id === id);
        if (emp) {
            setName(emp.name);
            setRole(emp.designation || emp.role || "Employee");
            setEmail(emp.email || "");
        } else {
            setName("");
            setRole("");
            setEmail("");
        }
    };

    const handleAIDraft = async () => {
        if (!name || !role) {
            toast.error("Please select a recipient or fill Name and Role first");
            return;
        }

        setDrafting(true);
        try {
            const r = await api.post("/documents/ai/draft-offer", {
                role: role,
                duration: duration,
                stipend: stipend
            });
            const fullLetter = (
                `Dear ${name},\n\n` +
                `We are pleased to extend an offer of employment for the position of ${role} (${offerType} Offer) at ParshWebCraft.\n\n` +
                `${r.data.content}\n\n` +
                `Please review the terms. If you accept this offer, please sign and return a copy of this letter.\n\n` +
                `Sincerely,\n` +
                `HR Department, ParshWebCraft`
            );
            setContent(fullLetter);
            toast.success("AI offer draft generated!");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDrafting(false);
        }
    };

    const handleGenerate = async (e) => {
        e.preventDefault();
        if (!name || !role) {
            toast.error("Name and Role are required");
            return;
        }

        setGenerating(true);
        try {
            await api.post("/documents/generate/offer_letter", {
                internId: recipientType === "intern" ? selectedInternId : undefined,
                employeeId: recipientType === "employee" ? selectedEmployeeId : undefined,
                content: content,
                custom_data: {
                    name: name,
                    role: role,
                    email: email,
                    offerType: offerType,
                    duration: duration,
                    stipend: stipend
                }
            });
            toast.success("Offer Letter generated successfully!");
            setName("");
            setRole("");
            setEmail("");
            setSelectedInternId("");
            setSelectedEmployeeId("");
            setContent("");
            init();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setGenerating(false);
        }
    };

    const handleRevoke = async (certId) => {
        if (!confirm(`Are you sure you want to revoke offer letter ${certId}?`)) return;
        try {
            await api.post(`/documents/${certId}/revoke`);
            toast.success("Offer Letter revoked successfully");
            init();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const handleDownload = (certId) => {
        window.open(`${api.defaults.baseURL}/verify/download/${certId}`, "_blank");
    };

    const handleOpenEdit = (doc) => {
        setEditingDoc(doc);
        setEditContent(doc.content || "");
    };

    const handleSaveRegenerate = async () => {
        if (!editingDoc) return;
        try {
            await api.post(`/documents/${editingDoc.certificateId}/regenerate`, {
                content: editContent
            });
            toast.success("Offer letter re-generated and updated successfully!");
            setEditingDoc(null);
            init();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6 p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-heading font-bold text-slate-800 dark:text-slate-100">Offer Letters</h1>
                <p className="text-sm text-muted-foreground">Draft and generate formal job and internship offers with electronic verification links.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Generation Wizard */}
                <div className="lg:col-span-1 glass p-6 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm self-start space-y-4">
                    <h3 className="font-heading font-bold text-slate-800 dark:text-slate-100 text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        <span>Generate Offer Letter</span>
                    </h3>

                    <form onSubmit={handleGenerate} className="space-y-4">
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Recipient Source</label>
                                <select
                                    value={recipientType}
                                    onChange={(e) => handleRecipientTypeChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                >
                                    <option value="custom">Custom Entry (Type Name)</option>
                                    <option value="intern">Select Intern</option>
                                    <option value="employee">Select Employee</option>
                                </select>
                            </div>

                            {recipientType === "intern" && (
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Choose Intern</label>
                                    <select
                                        required
                                        value={selectedInternId}
                                        onChange={(e) => handleInternSelect(e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    >
                                        <option value="">-- Choose Intern --</option>
                                        {interns.map((i) => (
                                            <option key={i.id} value={i.id}>{i.fullName} ({i.role})</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {recipientType === "employee" && (
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Choose Employee</label>
                                    <select
                                        required
                                        value={selectedEmployeeId}
                                        onChange={(e) => handleEmployeeSelect(e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    >
                                        <option value="">-- Choose Employee --</option>
                                        {employees.map((emp) => (
                                            <option key={emp.id} value={emp.id}>{emp.name} ({emp.designation || emp.role})</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-200 dark:border-white/10">
                                <div className="col-span-2">
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Recipient Full Name</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. Rahul Verma"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Recipient Email</label>
                                    <input
                                        type="email"
                                        required
                                        placeholder="e.g. rahul@example.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Job Role / Title</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. Software Engineer"
                                        value={role}
                                        onChange={(e) => setRole(e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Offer Type</label>
                                    <select
                                        value={offerType}
                                        onChange={(e) => setOfferType(e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    >
                                        <option value="Internship">Internship Offer</option>
                                        <option value="Full-Time">Full-Time Offer</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Duration</label>
                                    <input
                                        type="text"
                                        value={duration}
                                        onChange={(e) => setDuration(e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Compensation / Stipend</label>
                                    <input
                                        type="text"
                                        value={stipend}
                                        onChange={(e) => setStipend(e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="pt-2 border-t border-slate-200 dark:border-white/10">
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400">Final Offer Content</label>
                                <button
                                    type="button"
                                    onClick={handleAIDraft}
                                    disabled={drafting || !name || !role}
                                    className="flex items-center gap-1 text-[11px] text-primary hover:text-primary-hover disabled:text-muted-foreground font-semibold"
                                >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    <span>{drafting ? "Drafting..." : "AI Draft Content"}</span>
                                </button>
                            </div>
                            <textarea
                                required
                                rows={8}
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground font-sans leading-relaxed"
                                placeholder="Draft offer terms first, then customize offer letter body text here..."
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={generating}
                            className="w-full flex justify-center items-center gap-2 bg-primary hover:bg-primary-hover disabled:bg-primary/50 text-white py-2.5 rounded-xl text-sm font-semibold shadow-md transition"
                        >
                            {generating ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <FileText className="w-4 h-4" />
                            )}
                            <span>Generate Offer</span>
                        </button>
                    </form>
                </div>

                {/* Issued Offers List */}
                <div className="lg:col-span-2 space-y-4">
                    <div className="glass p-4 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm flex items-center justify-between">
                        <span className="font-heading font-semibold text-slate-800 dark:text-slate-100 text-sm">Issued Offer Letters ({documents.length})</span>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : documents.length === 0 ? (
                        <div className="glass py-16 text-center rounded-2xl border border-slate-200 dark:border-white/10">
                            <p className="text-muted-foreground text-sm">No offer letters generated yet.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {documents.map((doc) => (
                                <div key={doc.id} className="glass p-5 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm flex flex-col justify-between">
                                    <div>
                                        <div className="flex justify-between items-start mb-3">
                                            <span className="font-mono text-xs text-primary font-bold">{doc.certificateId}</span>
                                            <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase
                                                ${doc.status === "active" ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" : "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400"}`}
                                            >
                                                {doc.status}
                                            </span>
                                        </div>

                                        <h4 className="font-heading font-bold text-slate-800 dark:text-slate-100 text-base">{doc.custom_data?.name}</h4>
                                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 font-medium">{doc.custom_data?.role} ({doc.custom_data?.offerType})</p>
                                        
                                        <p className="text-[11px] text-muted-foreground line-clamp-3 mt-3 bg-slate-50 dark:bg-slate-900 p-2.5 rounded border border-slate-100 dark:border-white/5 whitespace-pre-line leading-relaxed font-sans">
                                            {doc.content}
                                        </p>
                                    </div>

                                    <div className="flex gap-2 border-t border-slate-200 dark:border-white/10 pt-4 mt-5">
                                        <button
                                            onClick={() => handleDownload(doc.certificateId)}
                                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 rounded-lg text-slate-700 dark:text-slate-200 transition"
                                        >
                                            <Download className="w-3.5 h-3.5" />
                                            <span>Download</span>
                                        </button>
                                        {doc.status === "active" && (
                                            <>
                                                <button
                                                    onClick={() => handleOpenEdit(doc)}
                                                    className="p-2 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg text-slate-500 hover:text-primary transition"
                                                    title="Edit & Regenerate"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleRevoke(doc.certificateId)}
                                                    className="p-2 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg text-slate-500 hover:text-rose-600 transition"
                                                    title="Revoke Offer"
                                                >
                                                    <ShieldAlert className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>

            {/* Edit / Regenerate Modal */}
            {editingDoc && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-2xl overflow-hidden border border-slate-200 dark:border-white/10 shadow-2xl animate-in fade-in zoom-in duration-200">
                        <div className="px-6 py-4 border-b border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/50 flex justify-between items-center">
                            <h2 className="font-heading font-bold text-lg text-slate-800 dark:text-slate-100">
                                Edit & Regenerate Offer Letter ({editingDoc.certificateId})
                            </h2>
                            <button
                                onClick={() => setEditingDoc(null)}
                                className="text-muted-foreground hover:text-foreground text-sm font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">Offer Letter Content</label>
                                <textarea
                                    rows={12}
                                    value={editContent}
                                    onChange={(e) => setEditContent(e.target.value)}
                                    className="w-full px-3 py-2 border border-slate-200 dark:border-white/10 rounded-xl bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground font-sans leading-relaxed"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/10">
                                <button
                                    type="button"
                                    onClick={() => setEditingDoc(null)}
                                    className="px-4 py-2 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveRegenerate}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-xl text-sm font-semibold shadow-md transition"
                                >
                                    <Save className="w-4 h-4" />
                                    <span>Save & Regenerate PDF</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
